// @flow

type RGB = { r: number, g: number, b: number };

export const convertHexColorToRGB = (hexString: string): RGB => {
  // chop off the preceding # if it exists
  const string = hexString.startsWith('#')
    ? hexString.replace('#', '')
    : hexString;

  const rChars = string.slice(0, 2);
  const gChars = string.slice(2, 4);
  const bChars = string.slice(4);

  const r = parseInt(rChars, 16);
  const g = parseInt(gChars, 16);
  const b = parseInt(bChars, 16);
  return { r, g, b };
};

// defined according to this reference:
// https://www.w3.org/TR/WCAG20/#relativeluminancedef
const calculateLuminance = ({ r, g, b }: RGB) => {
  const [red, green, blue] = [r, g, b].map(color => {
    const colorRatio = color / 255;
    return colorRatio <= 0.03928
      ? colorRatio / 12.92
      : Math.pow((colorRatio + 0.055) / 1.055, 2.4);
  });
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
};

export const getContrast = (lighterColor: RGB, darkerColor: RGB) => {
  // divided by .05 here according to this reference:
  // https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
  const lighterColorLuminance = calculateLuminance(lighterColor);
  const darkerColorLuminance = calculateLuminance(darkerColor);
  const contrast =
    (lighterColorLuminance + 0.05) / (darkerColorLuminance + 0.05);
  // because floating points in JS are silly, round to 2 decimal points.
  return Math.round(contrast * 100) / 100;
};

// Reference: https://webaim.org/articles/contrast/#ratio
// This function assumes that text/foreground is passed first and
// background is passed second.
export const isContrastWithinW3CRange = (
  lighterColor: RGB,
  darkerColor: RGB
) => {
  const contrast = getContrast(lighterColor, darkerColor);
  return {
    // https://www.w3.org/TR/WCAG21/#contrast-minimum
    aa: {
      base: contrast >= 4.5,
      largeText: contrast >= 3,
    },
    // https://www.w3.org/TR/WCAG21/#contrast-enhanced
    aaa: {
      base: contrast >= 7,
      largeText: contrast >= 4.5,
    },
    nonText: {
      base: contrast >= 3,
    },
  };
};
